import type {
  AgencyUserCreate,
  AgencyUserUpdate,
} from '@/modules/agencies/types/agencyUser'
import type { AgencyUserFormFieldErrors } from '@/modules/agencies/types/agencyUserForm'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

type CreateValidationResult =
  | { ok: true; value: AgencyUserCreate }
  | { ok: false; fieldErrors: AgencyUserFormFieldErrors }

type UpdateValidationResult =
  | { ok: true; value: AgencyUserUpdate }
  | { ok: false; fieldErrors: AgencyUserFormFieldErrors }

export function validateAgencyUserCreate(fields: {
  firstName: string
  lastName: string
  email: string
  password: string
  role: string
}): CreateValidationResult {
  const firstName = fields.firstName.trim()
  const lastName = fields.lastName.trim()
  const email = fields.email.trim().toLowerCase()
  const password = fields.password
  const role = fields.role.trim()
  const fieldErrors: AgencyUserFormFieldErrors = {}

  if (!firstName) {
    fieldErrors.firstName = 'First name is required.'
  }
  if (!lastName) {
    fieldErrors.lastName = 'Last name is required.'
  }
  if (!email) {
    fieldErrors.email = 'Email is required.'
  } else if (!EMAIL_PATTERN.test(email)) {
    fieldErrors.email = 'Enter a valid email address.'
  }
  if (!password) {
    fieldErrors.password = 'Password is required.'
  } else if (password.length < 8) {
    fieldErrors.password = 'Password must be at least 8 characters.'
  }
  if (!role) {
    fieldErrors.role = 'Role is required.'
  }

  if (Object.keys(fieldErrors).length > 0) {
    return { ok: false, fieldErrors }
  }

  return {
    ok: true,
    value: { firstName, lastName, email, password, role },
  }
}

export function validateAgencyUserUpdate(fields: {
  firstName: string
  lastName: string
  email: string
  password: string
  role: string
  status: string
}): UpdateValidationResult {
  const firstName = fields.firstName.trim()
  const lastName = fields.lastName.trim()
  const email = fields.email.trim().toLowerCase()
  const password = fields.password
  const role = fields.role.trim()
  const status = fields.status.trim()
  const fieldErrors: AgencyUserFormFieldErrors = {}

  if (!firstName) {
    fieldErrors.firstName = 'First name is required.'
  }
  if (!lastName) {
    fieldErrors.lastName = 'Last name is required.'
  }
  if (!email) {
    fieldErrors.email = 'Email is required.'
  } else if (!EMAIL_PATTERN.test(email)) {
    fieldErrors.email = 'Enter a valid email address.'
  }
  if (password && password.length < 8) {
    fieldErrors.password = 'Password must be at least 8 characters.'
  }
  if (!role) {
    fieldErrors.role = 'Role is required.'
  }
  if (!status) {
    fieldErrors.status = 'Status is required.'
  }

  if (Object.keys(fieldErrors).length > 0) {
    return { ok: false, fieldErrors }
  }

  const value: AgencyUserUpdate = {
    firstName,
    lastName,
    email,
    role,
    status,
  }

  if (password) {
    value.password = password
  }

  return { ok: true, value }
}
